import { uuid } from './ids';
import { installPageviewCapture, NO_CAPTURE, type PageviewCapture } from './pageviews';
import { EventQueue } from './queue';
import { SessionManager } from './session';
import { createStore, type KVStore } from './storage';
import { MAX_BATCH_SIZE, Transport } from './transport';
import type { AnalyticsEvent, EventProperties, NarratoConfig } from './types';
import { invalidEventReason, redactUrl } from './validate';
import { SDK_VERSION } from './version';

const ANONYMOUS_ID_KEY = 'narrato_anonymous_id';
const USER_ID_KEY = 'narrato_user_id';

const DEFAULTS = {
  host: 'https://api.narrato.app',
  flushAt: 20,
  flushInterval: 10_000,
  sessionTimeout: 30 * 60_000,
  autoPageviews: true,
  maxQueueSize: 1000,
  appVersion: '',
  debug: false,
};

const MAX_RETRY_BACKOFF_MS = 30_000;

/** The browser's referrer, redacted; a page of this site is no arrival. */
function arrivalReferrer(): string {
  if (typeof document === 'undefined' || !document.referrer) return '';
  try {
    if (new URL(document.referrer).origin === location.origin) return '';
  } catch {
    return '';
  }
  return redactUrl(document.referrer);
}

/**
 * The Narrato analytics client. Most apps use the singleton API from the
 * package root (init/track/identify/page); construct this directly only
 * when you need multiple isolated clients (e.g. tests).
 */
export class NarratoClient {
  private config: Required<NarratoConfig>;
  private store: KVStore;
  private session: SessionManager;
  private queue: EventQueue;
  private transport: Transport;
  private anonymousId: string;
  private userId: string | null;
  private flushTimer: ReturnType<typeof setInterval> | undefined;
  private pageviews: PageviewCapture = NO_CAPTURE;
  private flushing = false;
  /** Ids of the events in the request currently in flight. */
  private inFlight: ReadonlySet<string> = new Set();
  private retryCount = 0;
  /** How this page load arrived ('' = direct or from this site), until a session takes it. */
  private arrivalReferrer = arrivalReferrer();
  private nextAttemptAt = 0;

  constructor(config: NarratoConfig) {
    if (!config.writeKey) {
      throw new Error('[narrato] init requires a writeKey');
    }
    this.config = { ...DEFAULTS, ...config };
    this.store = createStore();
    this.session = new SessionManager(this.store, this.config.sessionTimeout);
    this.queue = new EventQueue(this.store, this.config.maxQueueSize);
    this.transport = new Transport(this.config.host, this.config.writeKey);

    this.anonymousId = this.store.get(ANONYMOUS_ID_KEY) ?? uuid();
    this.store.set(ANONYMOUS_ID_KEY, this.anonymousId);
    this.userId = this.store.get(USER_ID_KEY);

    this.flushTimer = setInterval(() => this.maybeFlush(), this.config.flushInterval);

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      if (this.config.autoPageviews) {
        this.pageviews = installPageviewCapture(this);
      }
    }

    this.log(`initialized (host: ${this.config.host}, sdk: ${SDK_VERSION})`);
  }

  /** Number of events waiting to be sent. */
  get pending(): number {
    return this.queue.size;
  }

  /** Queue a custom event. Flushes immediately once flushAt is reached. */
  track(eventName: string, properties: EventProperties = {}): void {
    this.enqueue(this.buildEvent(eventName, properties));
  }

  /**
   * Associate subsequent events with a known user. Emits an `identify`
   * event carrying the traits so identity changes are visible in the data.
   */
  identify(userId: string, traits: EventProperties = {}): void {
    this.userId = userId;
    this.store.set(USER_ID_KEY, userId);
    this.track('identify', traits);
  }

  /** Track a page view. URL/title/referrer are captured automatically. */
  page(properties: EventProperties = {}): void {
    const base = this.locationProperties();
    if (typeof document !== 'undefined') {
      base.page_title = document.title;
      base.referrer = redactUrl(document.referrer);
    }
    this.track('page_view', { ...base, ...properties });
  }

  /**
   * @internal An SPA navigation's page view. The URL, timestamp and session
   * are read now, at the navigation. Routers render the new `<title>` later,
   * so the title comes through the returned commit, which queues the event
   * (`undefined` = title unknown, sent without one). `referrer` is the
   * in-app page being left: `document.referrer` never changes after load.
   */
  beginPageview(referrer: string): (title: string | undefined) => void {
    const event = this.buildEvent('page_view', {
      ...this.locationProperties(),
      referrer: redactUrl(referrer),
    });
    return (title) => {
      if (title !== undefined) event.properties.page_title = title;
      this.enqueue(event);
    };
  }

  /** Clear identity on logout: new anonymous id, new session. */
  reset(): void {
    this.userId = null;
    this.store.remove(USER_ID_KEY);
    this.anonymousId = uuid();
    this.store.set(ANONYMOUS_ID_KEY, this.anonymousId);
    this.session.reset();
    this.log('reset');
  }

  /** Send every queued event now. Resolves when the queue is drained or a send fails. */
  async flush(): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      while (this.queue.size > 0) {
        const batch = this.queue.peek(MAX_BATCH_SIZE);
        const ids = new Set(batch.map((e) => e.event_id));
        this.inFlight = ids;
        const { status, retryAfterMs = 0 } = await this.transport.sendBatch(batch);
        if (status === 'sent') {
          this.queue.remove(ids);
          this.retryCount = 0;
          this.nextAttemptAt = 0;
          this.log(`flushed ${batch.length} events`);
        } else if (status === 'drop') {
          this.queue.remove(ids);
          this.warn(`server rejected a batch of ${batch.length} events; dropped`);
        } else {
          this.retryCount += 1;
          const backoff = Math.max(
            retryAfterMs,
            Math.min(MAX_RETRY_BACKOFF_MS, 1000 * 2 ** (this.retryCount - 1)),
          );
          this.nextAttemptAt = Date.now() + backoff;
          this.warn(`send failed; retrying in ${backoff}ms (${this.queue.size} queued)`);
          break;
        }
      }
    } finally {
      this.flushing = false;
      this.inFlight = new Set();
    }
  }

  /** Stop timers and listeners (tests / HMR). Queued events stay persisted. */
  shutdown(): void {
    if (this.flushTimer !== undefined) {
      clearInterval(this.flushTimer);
      this.flushTimer = undefined;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.handlePageHide);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.pageviews.uninstall();
  }

  private buildEvent(eventName: string, properties: EventProperties): AnalyticsEvent {
    // Only the first session of a page load arrived via the browser's
    // referrer: one renewed later (idle, reset) didn't arrive from anywhere.
    const session = this.session.touch(Date.now(), this.arrivalReferrer);
    this.arrivalReferrer = '';
    // Page views carry how the session arrived, for acquisition: `referrer`
    // is the page before this one, which in-app is the site itself. A
    // session saved before 0.1.3 has none, so it sends none (not "direct").
    if (eventName === 'page_view' && typeof session.referrer === 'string') {
      properties = { initial_referrer: session.referrer, ...properties };
    }
    const event: AnalyticsEvent = {
      event_id: uuid(),
      event_name: eventName,
      user_id: this.userId ?? this.anonymousId,
      timestamp: new Date().toISOString(),
      platform: 'web',
      app_version: this.config.appVersion,
      sdk_version: SDK_VERSION,
      session_id: session.id,
      device_id: this.anonymousId,
      properties,
    };
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      event.user_agent = navigator.userAgent;
    }
    return event;
  }

  // Credential-looking query/fragment parameters are redacted.
  private locationProperties(): EventProperties {
    if (typeof location === 'undefined') return {};
    return {
      page_url: redactUrl(location.href),
      path: location.pathname,
      search: redactUrl(location.search),
    };
  }

  private enqueue(event: AnalyticsEvent): void {
    // The server rejects a whole batch for one bad event, so drop just this
    // one, loudly: it's a bug in the calling code, not a transient failure.
    const invalid = invalidEventReason(event.event_name, event.properties);
    if (invalid) {
      console.warn(`[narrato] event "${event.event_name}" not tracked: ${invalid}`);
      return;
    }
    const queued = this.queue.push(event);
    this.log(`track ${event.event_name} (queued: ${queued})`);
    if (queued >= this.config.flushAt) {
      this.maybeFlush();
    }
  }

  private maybeFlush(): void {
    if (Date.now() < this.nextAttemptAt) return;
    void this.flush();
  }

  private handlePageHide = (): void => {
    // A navigation still waiting for its title goes out with this beacon,
    // untitled if need be: on tab-hidden too, since mobile browsers often
    // kill a backgrounded page without a pagehide.
    this.pageviews.settle();
    // Events in a request still in flight stay queued: that request removes
    // them on success, so beaconing them too would send them twice.
    const events = this.queue.drainExcept(this.inFlight);
    if (events.length === 0) return;
    // What the beacon didn't take goes back in the queue for the next page load.
    const refused = this.transport.beacon(events);
    if (refused.length > 0) this.queue.push(...refused);
    this.log(`page hidden; beaconed ${events.length - refused.length} events`);
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.handlePageHide();
    }
  };

  private log(message: string): void {
    if (this.config.debug) console.debug(`[narrato] ${message}`);
  }

  private warn(message: string): void {
    if (this.config.debug) console.warn(`[narrato] ${message}`);
  }
}
